import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { state } from './state.js';

/* ═══════════════════════════════════════════
   § 4. LOAD GLB ASSET
   ═══════════════════════════════════════════ */

// ── Loader progress UI helper ──────────────
function _ldr(pct, msg) {
  const fill   = document.getElementById('loader-fill');
  const status = document.getElementById('loader-status');
  if (fill)   fill.style.width    = pct + '%';
  if (status) status.textContent  = msg;
}

// ── FOV-based camera framing ───────────────
// Exported so render-loop.js can call it on window resize.
// Reads map dimensions from state (_mapSizeX / _mapSizeZ set during load).
export function computeFramedPositions(camera) {
  const fovRad = camera.fov * (Math.PI / 360); // half-FOV in radians
  const aspect = window.innerWidth / window.innerHeight;

  // Use the widest map dimension adjusted for aspect ratio
  const hFov = fovRad; // vertical half-FOV
  const wFov = Math.atan(Math.tan(hFov) * aspect); // horizontal half-FOV

  // Distance needed to frame the map in each axis
  const distV = (state._mapSizeZ / 2) / Math.tan(hFov); // vertical framing (depth of map)
  const distH = (state._mapSizeX / 2) / Math.tan(wFov); // horizontal framing (width of map)
  let frameDist = Math.max(distV, distH);

  // 20% padding
  frameDist *= 1.2;

  // Portrait mobile gets extra breathing room
  if (aspect < 1) frameDist *= 1.15;

  // Tablet Portrait Mode Aspect Ratio Multiplier
  const isTabletPortrait = window.innerWidth >= 768 && window.innerWidth <= 1024 && aspect < 1;
  if (isTabletPortrait) {
    frameDist *= 1.3; // Increase distance by 30% to prevent map edges being cut off
  }

  // 3D perspective view (~45° pitch)
  const pitch3D = Math.PI / 4;
  state.default3DPos = state.baseCenter.clone().add(new THREE.Vector3(
    0,
    frameDist * Math.sin(pitch3D),
    frameDist * Math.cos(pitch3D)
  ));

  // Top-down view (straight above)
  state.defaultTopPos = state.baseCenter.clone().add(new THREE.Vector3(0, frameDist, 0.1));
}

// ── Main loader entry point ────────────────
export async function loadAssets(active_project, ctx) {
  const { scene, camera, renderer, controls } = ctx;
  const loader     = new GLTFLoader();
  const dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
  loader.setDRACOLoader(dracoLoader);
  const _plotCells = document.querySelectorAll('.plot-cell');

  _ldr(10, 'INITIALIZING 3D ENGINE');

  try {
    _ldr(20, 'DOWNLOADING 3D MAP');
    const gltf = await loader.loadAsync(
      `https://assets.plotos.in/project-assets/${active_project}/models/${active_project}_3d_model.glb?t=` + Date.now()
    );

    // Center the entire map to world origin
    const box    = new THREE.Box3().setFromObject(gltf.scene);
    const center = box.getCenter(new THREE.Vector3());
    const size   = box.getSize(new THREE.Vector3());

    gltf.scene.position.x += (gltf.scene.position.x - center.x);
    gltf.scene.position.y += (gltf.scene.position.y - center.y);
    gltf.scene.position.z += (gltf.scene.position.z - center.z);

    state.baseCenter.copy(new THREE.Vector3(0, 0, 0)); // since we centered it

    // ── Auto-Frame: FOV-based camera framing with aspect ratio correction ──
    state._maxDim  = Math.max(size.x, size.y, size.z);
    state._mapSizeX = size.x;
    state._mapSizeZ = size.z;

    computeFramedPositions(camera);

    camera.position.copy(state.default3DPos);
    controls.target.copy(state.baseCenter);
    _ldr(50, 'PROCESSING PLOT MESHES');

    // Deep Mesh Traversal & Material Cloning
    let _meshIdx = 0;
    gltf.scene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow    = true;
        child.receiveShadow = true;

        // Find if this mesh belongs to a MAT_PLOT_ group
        let plotName = null;
        let p = child;
        while (p) {
          if (p.name && p.name.startsWith('MAT_PLOT_')) { plotName = p.name; break; }
          p = p.parent;
        }

        if (plotName) {
          if (child.material) {
            child.material = child.material.clone();
            // Optimize material for UI colors to "pop"
            if (child.material.isMeshStandardMaterial || child.material.isMeshPhysicalMaterial) {
              child.material.roughness = 0.8;
              child.material.metalness = 0.1;
            }
            state.originalColors.set(child.uuid, '#' + child.material.color.getHexString());

            // Precompute distance for wave effect
            child.geometry.computeBoundingBox();
            const boxCenter = new THREE.Vector3();
            child.geometry.boundingBox.getCenter(boxCenter);
            child.updateMatrixWorld(true);
            boxCenter.applyMatrix4(child.matrixWorld);
            child.userData.dist = boxCenter.length();
          }

          if (!state.plotMeshes.has(plotName)) state.plotMeshes.set(plotName, []);
          state.plotMeshes.get(plotName).push(child);
          // Fill loader plot cells sequentially
          if (_meshIdx < _plotCells.length) _plotCells[_meshIdx].classList.add('filled');
          _meshIdx++;
        }
      }
    });
    // Fill remaining cells
    _plotCells.forEach(c => c.classList.add('filled'));
    _ldr(80, 'BUILDING SCENE');

    scene.add(gltf.scene);
    _ldr(85, 'COMPILING SHADERS');

    // ── GPU WARM-UP: Render frames behind loader to force shader compilation,
    //    shadow map baking, and texture upload. User sees nothing — loader covers it.
    renderer.compile(scene, camera);          // force WebGL shader compile
    renderer.render(scene, camera);           // first render — triggers all GPU uploads
    _ldr(90, 'BAKING SHADOWS');

    // Render 30 warm-up frames to fully stabilize the pipeline
    const WARMUP_FRAMES = 30;
    for (let i = 0; i < WARMUP_FRAMES; i++) {
      controls.update();
      renderer.render(scene, camera);
      // Yield to browser every 5 frames so the loader UI stays responsive
      if (i % 5 === 0) await new Promise(r => setTimeout(r, 0));
    }
    _ldr(95, 'SYNCING LIVE DATA');

    // ── ADAPTIVE QUALITY CASCADE (AQC) ────────
    // Industry-standard progressive quality reduction for weak devices.
    // Capable devices pass the stable check in <500ms and never trigger AQC.
    // Weak devices that timeout get progressive reductions:
    //   Native → 75% (standard DRS floor) → 60% → 50% (emergency floor)
    // Shadow maps also downscale at each step to relieve GPU fill pressure.
    let _stableCount = 0;
    const TARGET_FRAME_MS = 1000 / 55; // ~18ms per frame (55fps threshold)
    const AQC_TIMEOUT_MS  = 6000;      // 6 seconds per quality tier
    let   _currentPR      = renderer.getPixelRatio();

    await new Promise(resolve => {
      let last         = performance.now();
      let attemptStart = performance.now();

      function checkFrame() {
        const now = performance.now();
        const dt  = now - last;
        last = now;
        controls.update();
        renderer.render(scene, camera);

        if (dt < TARGET_FRAME_MS) {
          _stableCount++;
        } else {
          _stableCount = Math.max(0, _stableCount - 1); // forgive occasional hiccup
        }

        // ── SUCCESS: Device can sustain target framerate ──
        if (_stableCount >= 10) {
          if (state._aqcActive) {
            const pct = ((_currentPR / state._nativeDPR) * 100).toFixed(0);
            console.log(`[AQC] Stabilized at ${pct}% quality (${_currentPR.toFixed(2)}×)`);
          }
          resolve();
          return;
        }

        // ── TIMEOUT: Reduce quality and retry ──
        if (now - attemptStart > AQC_TIMEOUT_MS) {
          // Calculate next tier: 75% of current, clamped to emergency floor
          const nextPR = Math.round(Math.max(_currentPR * 0.75, state._AQC_EMERGENCY_FLOOR) * 100) / 100;

          if (nextPR < _currentPR) {
            _currentPR = nextPR;
            renderer.setPixelRatio(_currentPR);
            renderer.setSize(window.innerWidth, window.innerHeight);

            // Downscale shadow maps to match quality tier
            const shadowRes = _currentPR <= state._AQC_EMERGENCY_FLOOR ? 512
                            : _currentPR <= state._DRS_FLOOR ? 1024 : 2048;
            scene.traverse(child => {
              if (child.isLight && child.shadow && child.shadow.mapSize) {
                child.shadow.mapSize.set(shadowRes, shadowRes);
                if (child.shadow.map) { child.shadow.map.dispose(); child.shadow.map = null; }
              }
            });

            state._aqcActive = true;
            _stableCount     = 0;
            attemptStart     = now;

            const pct = ((_currentPR / state._nativeDPR) * 100).toFixed(0);
            console.log(`[AQC] Device struggling — reducing to ${pct}% quality (${_currentPR.toFixed(2)}×, shadows: ${shadowRes}px)`);
            _ldr(92, `OPTIMIZING FOR YOUR DEVICE (${pct}%)`);

            requestAnimationFrame(checkFrame);
          } else {
            // Absolute minimum reached — force through
            console.log(`[AQC] Emergency floor reached (${_currentPR.toFixed(2)}×) — proceeding`);
            _ldr(93, 'OPTIMIZATION COMPLETE');
            state._aqcActive = true;
            resolve();
          }
          return;
        }

        requestAnimationFrame(checkFrame);
      }
      requestAnimationFrame(checkFrame);
    });

    _ldr(100, 'READY');
    // Brief pause so user sees "READY" before the curtain lifts
    await new Promise(r => setTimeout(r, 300));
    document.getElementById('loader').classList.add('done');
    console.log('[Native Three.js] Scene loaded successfully.');
    console.log(`[Native Three.js] Indexed ${state.plotMeshes.size} plot groups.`);

  } catch (e) {
    console.error('[Native Three.js] Load failed:', e);
    _ldr(0, 'ASSET LOAD FAILED — PLEASE REFRESH');
    throw e; // re-throw so main.js can halt the init sequence
  }
}
